// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionStringBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Connection String Builder component
 */
import 'share/utils/Izenda.StringUtilities';
import React, { Component } from 'react';
import { observable, action } from 'mobx';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import localizer from 'share/localization/Izenda.Localizer';

import DropDown from 'share/components/controls/Izenda.DropDown';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import constants from 'logic/data/Izenda.ConnectionStringBuilderConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';
import IzendaSwitch from 'share/components/controls/Izenda.Switch';
import { toPairs, getOr, isEmpty, isNil, flattenDeep, merge } from 'lodash/fp';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';


const InputValidator = Validator()(Input);

const LoadingComponent = () => (
  <div className="u_fullSize">
    <ScrollBox>{ReportPartUtils.renderLazyLoadingBar()}</ScrollBox>
  </div>
);

@connect()
export default class ConnectionStringBuilder extends Component {
  @observable isConnectionStringSecure = true;
  @observable isConnectionStringMode = false;
  @observable connectionString = '';
  @observable connectionStringEncrypted = '';
  @observable serverName = '';
  @observable database = '';
  @observable authentication = '';
  @observable login = '';
  @observable password = '';
  @observable passwordEncrypted = '';
  @observable isPasswordSecure = true;
  @observable options = '';
  initialConnectionString = '';

  static propTypes = {
    serverName: PropTypes.string,
    database: PropTypes.string,
    login: PropTypes.string,
    password: PropTypes.string,
    options: PropTypes.string,
    onOk: PropTypes.func.isRequired,
    connectionInfo: PropTypes.object,
    isConnectionStringEnabled: PropTypes.bool,
    connectionDetailStore: PropTypes.any
  };

  static defaultProps = {
    serverName: '',
    database: '',
    login: '',
    password: '',
    options: '',
    modalId: 'myModal',
    connectionInfo: null,
    isConnectionStringEnabled: false
  };

  state = {
    isLoading: false
  };

  constructor(props) {
    super(props);
    const connectionInfo = props.connectionInfo || {};
    this.connectionString = connectionInfo.connectionString || '';
    this.isConnectionStringSecure = !isEmpty(this.connectionString);
    this.connectionStringEncrypted = this.isConnectionStringSecure ? '************************************************************' : '';
    this.serverName = connectionInfo.serverName || '';
    this.database = connectionInfo.database || '';
    this.authentication = constants.AUTHENTICATION_TYPES['Standard'].value || '';
    this.login = connectionInfo.login || '';
    this.password = connectionInfo.password || '';
    this.isPasswordSecure = !isEmpty(this.connectionString);
    this.passwordEncrypted = this.isPasswordSecure ? '**********' : '';
    this.options = connectionInfo.options || '';
  }

  componentDidMount() {
    this.setState({ isLoading: true });
    this.loadConnectionStringData().then(result => {
      if (isNil(result))
        return null;
      const connectionStringData = getOr(null, '[1]', flattenDeep(result));
      if (isEmpty(connectionStringData))
        return null;
      this.initialConnectionString = connectionStringData.connectionString;
      this.connectionString = connectionStringData.connectionString;
      this.isConnectionStringSecure = !isEmpty(this.connectionString);
      this.connectionStringEncrypted = this.isConnectionStringSecure ? '************************************************************' : '';
      this.serverName = connectionStringData.serverName;
      this.database = connectionStringData.databaseName;
      this.login = connectionStringData.login;
      this.password = connectionStringData.password;
      this.isPasswordSecure = !isEmpty(this.connectionString);
      this.passwordEncrypted = this.isPasswordSecure ? '**********' : '';
      this.options = connectionStringData.options;
    }).finally(() => this.setState({ isLoading: false }));
  }

  /**
   * Load data from connection string.
   * @returns {Promise}
   */
  loadConnectionStringData() {
    const connectionDetailStore = getOr(null, 'props.connectionDetailStore', this);
    if (isNil(connectionDetailStore))
      return Promise.resolve(null);
    const { item } = connectionDetailStore;

    return new Promise((resolve, reject) => {
      connectionDetailStore
        .loadConnectionStringData(item)
        .then(
          result => {
            if (result.success) {
              resolve(toPairs(result.data));
            } else {
              reject(result);
            }
          },
          err => {
            reject(err);
          }
        )
    });
  }

  /**
   * Handle onChange for ServerName
   * @param {Event} e
   */
  handleServerNameInput = e => {
    this.serverName = e.target.value;
  };

  /**
   * Handler onFocus for connection string
   * @param {Event} e
   */
  handleConnectionStringInputFocus = e => {
    if (this.isConnectionStringSecure) {
      this.connectionString = '';
      this.connectionStringEncrypted = '';
      this.isConnectionStringSecure = false;
    }
  };

  /**
   * Handler onChange for Connection string
   * @param {Event} e
   */
  @action
  handleConnectionStringInput = e => {
    this.connectionString = e.target.value;
  };

  /**
   * Handle onChange for Database
   * @param {Event} e
   */
  handleDatabaseInput = e => {
    this.database = e.target.value;
  };

  /**
   * Handle onChange for Authentication
   * @param {Event} e
   */
  handleAuthenticationInput = e => {
    this.authentication = e.target.value;
  };

  /**
   * Handle onChange for Login
   * @param {Event} e
   */
  handleLoginInput = e => {
    this.login = e.target.value;
  };

  /**
   * Handler onFocus for password
   * @param {Event} e
   */
  handlePasswordInputFocus = e => {
    this.password = '';
    this.passwordEncrypted = '';
    this.isPasswordSecure = false;
  };

  /**
   * Handle onChange for Password
   * @param {Event} e
   */
  handlePasswordInput = e => {
    this.password = e.target.value;
  };

  /**
   * Handle onChange for Options
   * @param {Event} e
   */
  handleOptionsInput = e => {
    this.options = e.target.value;
  };

  /**
   * Build options for the buildAuthenticationOptions element
   * @returns {Object}
   */
  buildAuthenticationOptions() {
    const serverType = this.props.serverType || '';
    const { AUTHENTICATION_TYPES } = constants;
    const options = [
      {
        value: AUTHENTICATION_TYPES['Standard'].value,
        label: String.format(AUTHENTICATION_TYPES['Standard'].displayPattern, serverType)
      },
      {
        value: AUTHENTICATION_TYPES['Trusted'].value,
        label: AUTHENTICATION_TYPES['Trusted'].displayPattern
      }
    ];

    return (
      <DropDown
        id='connectionStringBuilder_Authentication'
        options={options}
        value={this.authentication}
        labelField='label'
        valueField='value'
        onChange={this.handleAuthenticationInput}
      />
    );
  }

  /**
   * Compose connection string
   */
  composeConnectionString() {
    const { password, login, database, serverName, authentication, isConnectionStringMode, connectionString } = this;
    if (isConnectionStringMode)
      return connectionString;

    const { AUTHENTICATION_TYPES } = constants;
    let connectionStringPattern = '';
    let result = undefined;
    if (authentication === AUTHENTICATION_TYPES['Standard'].value) {
      connectionStringPattern = AUTHENTICATION_TYPES['Standard'].connectionStringPattern;
    } else if (authentication === AUTHENTICATION_TYPES['Trusted'].value) {
      connectionStringPattern = AUTHENTICATION_TYPES['Trusted'].connectionStringPattern;
    }
    if (connectionStringPattern !== '') {
      result = String.format(connectionStringPattern, serverName, database, login, password);
    }

    return result;
  }

  /**
   * Custom connection string validation. Calls perform connection: if all API validations were successfull and
   * we were able to connect to database: validation is considered to be passed.
   * @returns {Promise<string>} Promise which returns error if fail.
   */
  connectionStringCustomValidation = () => {
    const { connectionDetailStore } = this.props;
    const newConnectionString = this.composeConnectionString();
    if (newConnectionString === this.initialConnectionString) {
      return Promise.resolve(null);
    }
    return connectionDetailStore
      .performConnection(newConnectionString)
      .then(result => {
        return getOr(undefined, 'messages[0].messages[0]', result);
      });
  };

  /**
   * Handle ok button's click event
   */
  @action
  onOkHandle() {
    const { validation, connectionDetailStore } = this.props;

    const nameBackup = connectionDetailStore.item.name;

    // Validation: performs basic form validation + advanced connection string API validation
    // (see connectionStringCustomValidation)
    this.setState({ isLoading: true });
    validation
      .validate()
      .then(validationResult => {
        if (validationResult) {
          this.props.onAccept();
          this.props.onOk && this.props.onOk(connectionDetailStore.item.connectionString);
        } else {
          // We don't need to turn off loading flag for successfull validation, because popup will be closed anyway.
          connectionDetailStore.item.name = nameBackup;
          this.setState({ isLoading: false });
        }
      });
    return false;
  }

  /**
   * Render loading bar
   */
  renderLoadingBar = () =>
    <div className="u-posAbsolute u-posFit u-bgWhite">
      <LoadingComponent/>
    </div>;

  /**
   * Render connection string builder
   * @returns {Object}
   */
  renderContent() {
    const {
      authentication, serverName, database, login, options, password, passwordEncrypted, isPasswordSecure, connectionString, isConnectionStringMode,
      connectionStringEncrypted, isConnectionStringSecure
    } = this;
    const { AUTHENTICATION_TYPES, SERVER_NAME_CONSTRAINT, DATABASE_CONSTRAINT, CONNECTION_STRING_CONSTRAINT } = constants;
    const { isConnectionStringEnabled } = this.props;

    const extendValidationFn = baseValidation => merge(
      baseValidation,
      {
        action: () => () => this.connectionStringCustomValidation()
      });


    const isWindowAuthenMode = authentication === AUTHENTICATION_TYPES['Trusted'].value;
    const isConnectionString = isConnectionStringEnabled && isConnectionStringMode;
    return (
      <form>
        {/*Toggle "connection string" <--> "form"*/}
        {isConnectionStringEnabled && (
          <IzendaSwitch
            offLabel={localizer.getText('CONNECTION_LABEL_CONNECTION_STRING')}
            onLabel={localizer.getText('CONNECTION_LABEL_CONNECTION_BUILDER')}
            value={isConnectionStringMode}
            onChange={() => this.isConnectionStringMode = !this.isConnectionStringMode}
          />
        )}
        {isConnectionStringEnabled && (
          <hr className='izenda-full-hr'/>
        )}

        {/*Connection string*/}
        {isConnectionString && (
          <div className='form-horizontal'>
            <div className='form-group'>
              <label className='control-label col-md-3 validation required'>
                {localizer.getText('CONNECTION_LABEL_CONNECTION_STRING')}
              </label>
              <div className='col-md-9'>
                <InputValidator
                  className='form-control'
                  name='connectionString'
                  onFocus={this.handleConnectionStringInputFocus}
                  onChange={this.handleConnectionStringInput}
                  value={isConnectionStringSecure ? connectionStringEncrypted : connectionString}
                  type={isConnectionStringSecure ? 'password' : 'text'}
                  maxLength='MAX'
                  validations={extendValidationFn(CONNECTION_STRING_CONSTRAINT)}
                  field='connectionString'
                />
              </div>
            </div>
          </div>
        )}

        {/*Connection string builder*/}
        {!isConnectionString && (
          <div className='form-horizontal'>
            <div className='form-group'>
              <label className='control-label col-md-3 validation required'>
                {localizer.getText('CONNECTION_STRING_BUILDER_LABEL_SERVER_NAME')}
              </label>
              <div className='col-md-9'>
                <InputValidator
                  className='form-control'
                  name='serverName'
                  onChange={this.handleServerNameInput}
                  value={serverName}
                  type='text'
                  validations={extendValidationFn(SERVER_NAME_CONSTRAINT)}
                  field='serverName'
                />
              </div>
            </div>
            <div className='form-group'>
              <label className='control-label col-md-3 validation required'>
                {localizer.getText('CONNECTION_STRING_BUILDER_LABEL_DATABASE')}
              </label>
              <div className='col-md-9'>
                <InputValidator
                  className='form-control'
                  name='database'
                  onChange={this.handleDatabaseInput}
                  value={database}
                  type='text'
                  validations={DATABASE_CONSTRAINT}
                  field='database'
                />
              </div>
            </div>
            <div className='form-group'>
              <label className='control-label col-md-3'>
                {localizer.getText('CONNECTION_STRING_BUILDER_TEXT_AUTHENTICATION')}
              </label>
              <div className='col-md-9'>{this.buildAuthenticationOptions()}</div>
            </div>
            <div className='form-group'>
              <label className='control-label col-md-3'>{localizer.getText('COMMON_LABEL_LOGIN')}</label>
              <div className='col-md-9'>
                <InputValidator
                  className='form-control text-box single-line'
                  name='Login'
                  type='text'
                  value={login}
                  onChange={this.handleLoginInput}
                  disabled={isWindowAuthenMode}
                />
              </div>
            </div>
            <div className='form-group'>
              <label className='control-label col-md-3'>{localizer.getText('COMMON_LABEL_PASSWORD')}</label>
              <div className='col-md-9'>
                <input
                  className='form-control text-box single-line'
                  name='Password'
                  type='password'
                  value={isPasswordSecure ? passwordEncrypted : password}
                  onFocus={this.handlePasswordInputFocus}
                  onChange={this.handlePasswordInput}
                  disabled={isWindowAuthenMode}
                />
              </div>
            </div>
            <div className='form-group'>
              <label className='control-label col-md-3'>{localizer.getText('COMMON_LABEL_OPTIONS')}</label>
              <div className='col-md-9'>
                <InputValidator
                  className='form-control text-box single-line'
                  name='Options'
                  type='text'
                  value={options}
                  onChange={this.handleOptionsInput}
                />
              </div>
            </div>
          </div>
        )}
      </form>
    );
  }

  /**
   * Render connection string builder modal
   * @returns {Object}
   */
  render() {
    const { isLoading } = this.state;

    return (
      <Popup visible onClosing={this.onClosing} type='none' autoClose={false}>
        <Popup.Header title={localizer.getText('CONNECTION_STRING_BUILDER_LABEL_DATABASE_CONNECTION')}/>
        <Popup.Body className="connection-string-builder-popup-content">
          {this.renderContent()}
          {isLoading && this.renderLoadingBar()}
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type='accept' disabled={isLoading}>
            {localizer.getText('COMMON_LABEL_OK')}
          </Popup.ConfirmationButton>
          <Popup.ConfirmationButton type='cancel' disabled={isLoading}>
            {localizer.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }

  /**
   * on closing
   * @param {Object} cb
   * @param {String} type
   */

  onClosing = (cb: any, type: String) => {
    if (type === 'accept') {
      this.onOkHandle();
    } else if (type === 'cancel') {
      this.props.onReject();
    }
  };
}

ConnectionStringBuilder.$popupName = 'ConnectionStringBuilder';



// WEBPACK FOOTER //
// ./ui/share/components/controls/connectionStringBuilder/Izenda.ConnectionStringBuilder.js